// src/components/MarketReleaseCard.jsx
// Page d'une collection : cadence de mise sur le marché des œuvres (à diffuser, réservées, retenues, diffusées), par année.
// Pour un estate qui contrôle le rythme de diffusion, ou une collection familiale écoulée progressivement.
// Information de planification : ce n'est ni une décision de vente ni un conseil. Textes en français, anglais, allemand.
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "../lib/supabaseClient";
import { RELEASE_STATUSES, summarizeRelease } from "../utils/marketRelease";

const COLORS = { to_release: "#b9892f", reserved: "#6b5fb5", withheld: "#6f695f", released: "#2f6b3f" };

const TEXT = {
  fr: {
    eyebrow: "Cadence", title: "Mise sur le marché", sub: "Planification interne du rythme de diffusion des œuvres ; ce n'est ni une décision de vente ni un conseil.",
    statuses: { to_release: "À diffuser", reserved: "Réservées", withheld: "Retenues", released: "Diffusées", unplanned: "Non planifiées" },
    byYear: "Par année", share: (p) => `${p} % des œuvres planifiées sont diffusées`, empty: "Aucune œuvre n'a encore de cadence : indiquez-la sur la fiche de chaque œuvre (onglet Détails).",
    of: (a, b) => `${a} sur ${b} œuvres`,
  },
  en: {
    eyebrow: "Release schedule", title: "Release to the market", sub: "Internal planning of the pace at which works are released; it is neither a sale decision nor advice.",
    statuses: { to_release: "To release", reserved: "Reserved", withheld: "Withheld", released: "Released", unplanned: "Not scheduled" },
    byYear: "By year", share: (p) => `${p}% of the scheduled works have been released`, empty: "No work has a release schedule yet: set it on each artwork record (Details tab).",
    of: (a, b) => `${a} of ${b} works`,
  },
  de: {
    eyebrow: "Taktung", title: "Markteinführung", sub: "Interne Planung des Tempos, in dem Werke auf den Markt kommen; weder eine Verkaufsentscheidung noch eine Beratung.",
    statuses: { to_release: "Freizugeben", reserved: "Reserviert", withheld: "Zurückgehalten", released: "Freigegeben", unplanned: "Nicht geplant" },
    byYear: "Nach Jahr", share: (p) => `${p} % der geplanten Werke sind freigegeben`, empty: "Noch kein Werk hat eine Taktung: Tragen Sie sie in der Akte jedes Werks ein (Reiter Details).",
    of: (a, b) => `${a} von ${b} Werken`,
  },
};

export default function MarketReleaseCard({ collectionId, alwaysShow = false }) {
  const { i18n } = useTranslation();
  const L = TEXT[String(i18n?.resolvedLanguage || i18n?.language || "fr").slice(0, 2)] || TEXT.fr;
  const [rows, setRows] = useState(null);

  useEffect(() => {
    let alive = true;
    if (!collectionId) return undefined;
    supabase.from("artworks_secure").select("id,market_release_status,market_release_year").eq("collection_id", collectionId).limit(5000)
      .then(({ data, error }) => { if (alive) setRows(error ? [] : (data || [])); })
      .catch(() => { if (alive) setRows([]); });
    return () => { alive = false; };
  }, [collectionId]);

  const s = useMemo(() => summarizeRelease(rows || []), [rows]);
  if (rows === null) return null;
  if (s.planned === 0 && !alwaysShow) return null;

  return (
    <div className="bg-white rounded-lg shadow p-5 mb-6 border border-gray-100">
      <p className="text-xs uppercase tracking-widest text-gray-500 mb-1">{L.eyebrow}</p>
      <h3 className="text-lg font-semibold text-gray-900" style={{ fontFamily: "Georgia, serif" }}>{L.title}</h3>
      <p className="text-xs text-gray-500 mb-3">{L.sub}</p>
      {s.planned === 0 ? (
        <p className="text-sm text-gray-500">{L.empty}</p>
      ) : (
        <>
          <div className="flex flex-wrap gap-2 mb-3">
            {[...RELEASE_STATUSES, "unplanned"].map((k) => (
              <span key={k} className="text-xs rounded-full border px-3 py-1" style={{ borderColor: COLORS[k] || "#e5e3df", color: COLORS[k] || "#6f695f" }}>
                {L.statuses[k]} · {s.byStatus[k]}
              </span>
            ))}
          </div>
          <p className="text-sm text-gray-700 mb-3">{L.share(s.releasedShare)} ({L.of(s.byStatus.released, s.planned)})</p>
          {s.byYear.length > 0 && (
            <>
              <p className="text-xs uppercase tracking-widest text-gray-500 mb-2">{L.byYear}</p>
              <div className="grid gap-2">
                {s.byYear.map((y) => {
                  const total = RELEASE_STATUSES.reduce((n, k) => n + y[k], 0) || 1;
                  return (
                    <div key={y.year} className="flex items-center gap-3 text-sm">
                      <span className="w-12 text-gray-700">{y.year}</span>
                      <div className="flex-1 flex h-3 rounded overflow-hidden bg-gray-100">
                        {RELEASE_STATUSES.map((k) => y[k] > 0 && (
                          <div key={k} title={`${L.statuses[k]} : ${y[k]}`} style={{ width: `${(y[k] / total) * 100}%`, background: COLORS[k] }} />
                        ))}
                      </div>
                      <span className="w-8 text-right text-gray-500">{total}</span>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
